import { Alert, Box, Button, Stack, TextField, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { LoadingComponent } from "@gui/widgets/LoadingComponent";
import { AsyncStatus } from "@gui/widgets/A11y";
import { PageLayout } from "@gui/widgets/PageLayout";
import { PasswordResetProps } from "./passwordreset.types";
import { usePasswordResetViewModel } from "./passwordreset.vm";

function Busy(props: { text: string }) {
	return (
		<Box display="flex" justifyContent="center" alignItems="center" aria-busy="true">
			<AsyncStatus state="loading" loadingText={props.text} />
			<LoadingComponent />
		</Box>
	);
}

/** Both halves of a password reset: asking for a link (no token), and choosing the new password (a valid one). */
export function PasswordReset(props: PasswordResetProps) {
	const vm = usePasswordResetViewModel(props);

	if (vm.isTokenValid) {
		return (
			<PageLayout width="form">
				<Stack spacing={2} component="form" noValidate onSubmit={vm.handlePasswordUpdate}>
					<Typography color="text.secondary">Enter your new password.</Typography>
					<TextField
						variant="outlined"
						error={!!vm.passwordError}
						helperText={vm.passwordError}
						required
						disabled={vm.loading}
						fullWidth
						name="password"
						label="New Password"
						type="password"
						id="password"
						autoComplete="new-password"
						onChange={vm.handlePasswordChange}
						onBlur={vm.handlePasswordChange}
						data-sentry-component="New Password Field"
					/>
					<TextField
						variant="outlined"
						error={!!vm.confirmPasswordError}
						helperText={vm.confirmPasswordError}
						required
						disabled={vm.loading}
						fullWidth
						name="Confirm password"
						label="Confirm Password"
						type="password"
						id="Confirm password"
						autoComplete="new-password"
						onChange={vm.handleConfirmPasswordChange}
						onBlur={vm.handleConfirmPasswordChange}
						data-sentry-component="Confirm Password Field"
					/>
					{/* This branch's error genuinely populates (`handlePasswordUpdate` sets it for an expired
					    link), so it is a real live region, in the error colour (#661). Above the submit, as on
					    every auth page. */}
					{vm.error && <Alert severity="error">{vm.error}</Alert>}
					<Button
						type="submit"
						fullWidth
						variant="contained"
						size="large"
						sx={{ mt: 1 }}
						disabled={vm.loading}
						data-sentry-component="Submit Button"
					>
						Update Password
					</Button>
					{vm.loading && <Busy text="Updating your password…" />}
				</Stack>
			</PageLayout>
		);
	}

	return (
		<PageLayout width="form">
			<Stack spacing={2} component="form" noValidate onSubmit={vm.handlePasswordReset}>
				{/* No error element in this branch on purpose: `handlePasswordReset` routes every failure
				    through `Oops.handleError` (a toast) and never calls `setError` with a message, so an
				    element bound to `vm.error` here would be permanently empty — the same dead shape
				    removed from Login. `vm.error` is shared with the update branch above, which does
				    populate it; rendering it here would surface that branch's message on this form. */}
				<Typography color="text.secondary">
					Enter the email for your ROGO account and we&rsquo;ll send you a link to reset your password.
				</Typography>
				<TextField
					variant="outlined"
					required
					fullWidth
					id="email"
					label="Email Address"
					name="email"
					autoComplete="email"
					autoFocus
					error={!!vm.emailError}
					helperText={vm.emailError}
					onChange={vm.handleEmailChange}
					onBlur={vm.handleEmailChange}
					data-sentry-component="Email Field"
				/>
				<Button
					type="submit"
					fullWidth
					variant="contained"
					size="large"
					sx={{ mt: 1 }}
					disabled={vm.loading}
					data-sentry-component="Submit Button"
				>
					Send Reset Link
				</Button>
				{vm.loading && <Busy text="Sending your reset link…" />}
				<Box display="flex" justifyContent="center">
					<Button component={RouterLink} to="/login" variant="text" disabled={vm.loading}>
						Back to sign in
					</Button>
				</Box>
			</Stack>
		</PageLayout>
	);
}
